import React, { useEffect, useRef } from 'react';

const COLORS = [
  'rgba(60, 60, 60, 0.4)',     // dark grey
  'rgba(80, 80, 80, 0.35)',    // medium-dark grey
  'rgba(50, 50, 50, 0.3)',     // darker grey
  'rgba(70, 70, 70, 0.3)',     // grey
  'rgba(55, 55, 55, 0.25)',    // subtle grey
];

const LINE_COUNT = 8;

function createLine(w, h, i) {
  const spacing = h / (LINE_COUNT + 1);
  const baseY = spacing * (i + 1);
  return {
    baseY,
    amplitude: 20 + Math.random() * 30,
    wavelength: 600 + Math.random() * 400,
    speed: 0.15 + Math.random() * 0.25,
    phase: Math.random() * Math.PI * 2,
    lineWidth: 0.5 + Math.random() * 1,
    color: COLORS[i % COLORS.length],
    // Each line has a small diagonal drift
    drift: (Math.random() - 0.5) * 0.3,
  };
}

function PipelineBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let lines = [];

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      lines = Array.from({ length: LINE_COUNT }, (_, i) =>
        createLine(window.innerWidth, window.innerHeight, i)
      );
    }

    function draw(time) {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const t = time * 0.001;

      ctx.clearRect(0, 0, w, h);

      for (const line of lines) {
        ctx.beginPath();
        ctx.strokeStyle = line.color;
        ctx.lineWidth = line.lineWidth;

        const segments = Math.ceil(w / 4);
        for (let s = 0; s <= segments; s++) {
          const x = (s / segments) * (w + 100) - 50;
          const y =
            line.baseY +
            Math.sin((x / line.wavelength) * Math.PI * 2 + line.phase + t * line.speed) *
              line.amplitude +
            Math.sin((x / (line.wavelength * 0.6)) * Math.PI * 2 - t * line.speed * 0.7) *
              (line.amplitude * 0.4) +
            line.drift * x * 0.05;

          if (s === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.stroke();

        // Flowing particle (bright dot travelling along the line)
        const particlePhase = (t * line.speed * 60 + line.phase * 100) % (w + 200) - 100;
        const px = particlePhase;
        const py =
          line.baseY +
          Math.sin((px / line.wavelength) * Math.PI * 2 + line.phase + t * line.speed) *
            line.amplitude +
          Math.sin((px / (line.wavelength * 0.6)) * Math.PI * 2 - t * line.speed * 0.7) *
            (line.amplitude * 0.4) +
          line.drift * px * 0.05;

        if (px > -50 && px < w + 50) {
          const grad = ctx.createRadialGradient(px, py, 0, px, py, 12);
          grad.addColorStop(0, line.color.replace(/[\d.]+\)$/, '0.8)'));
          grad.addColorStop(1, 'transparent');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(px, py, 12, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animId = requestAnimationFrame(draw);
    }

    resize();
    animId = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 z-[-1] pointer-events-none"
    />
  );
}

export default PipelineBackground;
